import { Link } from "react-router-dom";
import { Car, Clock } from "lucide-react";
import { Card, CardBody } from "./ui/Card";
import { Badge } from "./ui/Badge";
import { StatusBadge } from "./StatusBadge";
import { arriveByIso, fmtDateShort, fmtTimeCt } from "../lib/dates";

interface Visit {
  id: string;
  title: string | null;
  provider_name_snapshot: string | null;
  provider_specialty_snapshot: string | null;
  facility_name_snapshot: string | null;
  starts_at: string;
  arrive_lead_minutes: number;
  visit_category: string | null;
  status: "open" | "accepted" | "cancelled" | "completed";
  ride_required: number;
  pending_approval?: number;
}

function monthLabel(iso: string): string {
  return new Intl.DateTimeFormat("en-US", { timeZone: "America/Chicago", month: "short" }).format(new Date(iso));
}

export function VisitCard({ visit }: { visit: Visit }) {
  const title = visit.provider_name_snapshot ?? visit.title ?? "Doctor visit";
  const subtitle = [visit.provider_specialty_snapshot, visit.facility_name_snapshot].filter(Boolean).join(" · ");
  const day = new Date(visit.starts_at).toLocaleString("en-US", { timeZone: "America/Chicago", day: "numeric" });
  const dow = fmtDateShort(visit.starts_at).split(" ")[0];

  return (
    <Link to={`/app/visits/${visit.id}`} className="block group">
      <Card interactive className="group-hover:border-primary/30">
        <CardBody className="!p-4 sm:!p-5">
          <div className="flex items-stretch gap-4">
            <div className="shrink-0 w-14 rounded-lg bg-primary-soft border border-primary/15 text-center py-2 flex flex-col justify-center">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-primary">
                {monthLabel(visit.starts_at)}
              </div>
              <div className="text-2xl font-bold tracking-tight leading-none text-primary mt-0.5">{day}</div>
              <div className="text-[10px] uppercase tracking-wider text-primary/70 mt-0.5">{dow}</div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <div className="font-semibold truncate text-[15px] sm:text-base">{title}</div>
                {visit.pending_approval ? (
                  <Badge tone="warning"><Clock className="h-3.5 w-3.5 mr-1 -ml-0.5" /> Pending</Badge>
                ) : (
                  <StatusBadge status={visit.status} />
                )}
              </div>
              {subtitle && <div className="text-sm text-text-muted truncate">{subtitle}</div>}
              <div className="mt-1.5 text-sm">
                <span className="font-medium text-text">
                  Arrive by {fmtTimeCt(arriveByIso(visit.starts_at, visit.arrive_lead_minutes))}
                </span>
                <span className="text-text-muted"> · Starts {fmtTimeCt(visit.starts_at)}</span>
              </div>
              {visit.ride_required ? (
                <div className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-warning bg-warning-soft border border-amber-200 px-2 py-0.5 rounded-full">
                  <Car className="h-3 w-3" /> Ride needed
                </div>
              ) : null}
            </div>
          </div>
        </CardBody>
      </Card>
    </Link>
  );
}
